
import jspm from '@/services/PrintManager';
import { LoadPersonenPayload } from '@/store/personen';
import { AclModul, AclPermission, ImpfPrintPayload, PatImpfungenFilter } from '@/store/types';
import { CompareFunction, DataTableHeaders } from '@/types';
import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';
import Karteieintrag from '@/types/Karteieintrag';
import Reiseplan from '@/types/Reiseplan';
import { Satzart } from '@/types/Satzart';
import User from '@/types/User';
import _ from 'lodash';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

    @Component({
        computed: {
            ...mapState('patient', [
                'patientData',
                'impfungen',
                'dialogs',
                'loadRechnung',
                'filter',
                'dt_load',
                'reiseplaene',
                'loaders',
                'gruppiert',
                'showAllePatientInfo'
            ]),
            ...mapState('globals', ['sortDate']),
            ...mapGetters('authentication', ['user', 'isAllowed', 'getSystemEinstellung']),
            ...mapGetters('personen', ['getPersonen']),
            ...mapState('warteliste', { wartelisteLoaders: 'loaders' }),
        },
        methods: {
            ...mapActions('patient', ['neueRechnung', 'loadImpfungen', 'downloadTextDatei', 'printImpfetikett']),
            ...mapMutations('patient', [
                'setNeueImpfung',
                'setFilters',
                'setDialogText',
                'setSelectedText',
                'setSelectedImpfeintrag',
                'setDialogImpfeintragEdit',
                'setDialogImpfungNachtrag',
                'setDialogReiseplan',
                'setDialogRezept',
                'setDialogLeistung',
                'setDialogDiagnose',
                'setDialogAnamnese',
                'setDialogAnamneseListItem',
                'setDialogBrief',
                'setDialogUntersuchung',
                'setSelectedReiseplan',
                'setSelectedRezepteintrag',
                'setSelectedLeistungseintrag',
                'setSelectedBrief',
                'setSelectedUntersuchung',
                'setSelectedDiagnose',
                'setSelectedAnamnese',
                'setDialogZurKassa'
            ]),
            ...mapActions('personen', ['loadPersonen']),
        },
    })
    export default class KarteiDataTable extends Vue {
        public user!: User | null;
        public isAllowed!: (moduleName: AclModul, permission: AclPermission | AclPermission[]) => boolean;
        public filter!: PatImpfungenFilter;
        public sortDate!: CompareFunction<Karteieintrag>;
        public reiseplaene!: Reiseplan[];
        public loaders!: { text: { downloads: number[] } };
        public impfungen!: Karteieintrag[];
        public gruppiert!: boolean;
        public getPersonen!: any;
        public setFilters!: (filters: PatImpfungenFilter) => void;
        public loadImpfungen!: () => void;
        public loadPersonen!: (payload?: LoadPersonenPayload) => void;
        public setSelectedReiseplan!: (plan: Reiseplan) => void;
        public setDialogReiseplan!: (visible: boolean) => void;
        public setSelectedImpfeintrag!: (eintrag: Karteieintrag) => void;
        public setDialogImpfeintragEdit!: (visible: boolean) => void;
        public setSelectedLeistungseintrag!: (eintrag: Karteieintrag) => void;
        public setDialogLeistung!: (visible: boolean) => void;
        public setSelectedText!: (eintrag: Karteieintrag) => void;
        public setDialogText!: (visible: boolean) => void;
        public setSelectedUntersuchung!: (eintrag: Karteieintrag) => void;
        public setDialogUntersuchung!: (visible: boolean) => void;
        public setDialogDiagnose!: (visible: boolean) => void;
        public setSelectedDiagnose!: (eintrag: Karteieintrag) => void;
        public setSelectedAnamnese!: (eintrag: Karteieintrag) => void;
        public setDialogAnamnese!: (visible: boolean) => void;
        public setDialogAnamneseListItem!: (check: boolean) => void;
        public setSelectedBrief!: (eintrag: Karteieintrag) => void;
        public setDialogBrief!: (visible: boolean) => void;
        public setSelectedRezepteintrag!: (eintrag: Karteieintrag) => void;
        public setDialogRezept!: (visible: boolean) => void;
        public printImpfetikett!: (payload: ImpfPrintPayload) => string | null;
        public getSystemEinstellung!: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T>;
        public patientData!: any;
 
        public search = '';
        public plaeneTemp: Reiseplan[] = [];

        public filters: PatImpfungenFilter = {
            satzarten: Satzart.values(),
            elga: false,
            gruppierung: false,
        };

        public Satzart = Satzart;
        public filterMenu = false;
        public dtExpand: Karteieintrag[] = [];

        public get headers(): DataTableHeaders<Karteieintrag, 'padding' | 'actions' | 'status' | 'data-table-expand'> {
            if (this.gruppiert) {
                return [
                    { text: '', value: 'padding', sortable: false, width: 200 },
                    { text: '', value: 'actions', sortable: false, width: 50 },
                    { text: 'Immunisierung', value: 'impfstoff_impfung', sortable: false },
                    { text: 'Datum', value: 'impfdatumDisplay', sortable: false, width: 110 },
                    { text: 'Impfung', value: 'textDisplay', sortable: false },
                    { text: '', value: 'data-table-expand' },
                ];
            }

            return [
                { text: '', value: 'actions', sortable: false, width: 50 },
                { text: '', value: 'status', sortable: false, width: 50, align: 'center' },
                { text: 'Datum', value: 'impfdatumDisplay', width: 90, sort: this.sortDate },
                { text: 'Text', value: 'textDisplay'},
                { text: 'Arzt', value: 'arztDisplay'},
                /*{ text: 'Impfstelle', value: 'impfstelleDisplay', width: 110 },*/
                { text: '', value: 'data-table-expand', width: 50},
            ];
        }

        public get items(): Karteieintrag[] {
            if (this.gruppiert) {
                return _
                    .chain(this.impfungen)
                    .groupBy('impfstoff_impfung')
                    .map((group) => {
                        const [first] = group;
                        return first;
                    })
                    .value();
            }
            return this.impfungen;
        }

        @Watch('patientData')
        public onChangePatientData(): void {
            this.filters = this.filter;
        }

        @Watch('patientData.patnr')
        public onChangePatientDataPatNr(): void {
            this.search = '';
        }

        public mounted(): void {
            this.filters.gruppierung = this.gruppiert;
            this.loadPersonen();
        }

        public onChangeFilters(): void {
            this.setFilters(this.filters);
            this.loadImpfungen();
            this.dtExpand = [];
        }

        public onClickPrint(patimpfungId: string, teilimpfungsKuerzel?: string): void {
            this.printImpfetikett({
                data: patimpfungId,
                paperName: 'Impfetikett',
                printRotation: jspm.printer?.manager.PrintRotation.None,
                teilimpfungsKuerzel,
            });
        }

        public handleArztDisplay(item: any): void {
            if (/\d/.test(item)) {
                var numberItem: number = +item;
                if (this.getPersonen.length && this.getPersonen.length > 0) {
                    for (var cnt = 0; this.getPersonen.length > cnt; cnt++) {
                        if (this.getPersonen[cnt].person_id === numberItem) {
                            return this.getPersonen[cnt].arzt;
                        }
                    }
                }
            } else {
                return item;
            }
        }

        public onClickFilter(all: boolean): void {
            this.search = '';

            this.filters.satzarten = all ? Satzart.values() : [];

            this.onChangeFilters();
        }

        public openEintrag(eintrag: Karteieintrag): void {
            switch (eintrag.satzart) {
                case Satzart.Plan: {
                    const plan = this.reiseplaene.find((plan) => plan.patplan_id === eintrag.patplan_id);
                    this.setSelectedReiseplan(plan as Reiseplan);
                    this.setDialogReiseplan(true);
                    break;
                }
                case Satzart.Impfung: {
                    this.setSelectedImpfeintrag(eintrag);
                    this.setDialogImpfeintragEdit(true);
                    break;
                }
                case Satzart.Leistung: {
                    this.setSelectedLeistungseintrag(eintrag);
                    this.setDialogLeistung(true);
                    break;
                }
                case Satzart.Text: {
                    this.setSelectedText(eintrag);
                    this.setDialogText(true);
                    break;
                }
                case Satzart.Untersuchung: {
                    this.setSelectedUntersuchung(eintrag);
                    this.setDialogUntersuchung(true);
                    break;
                }
                case Satzart.Diagnose: {
                    this.setSelectedDiagnose(eintrag);
                    this.setDialogDiagnose(true);
                    break;
                }
                case Satzart.Anamnese: {
                    this.setSelectedAnamnese(eintrag);
                    this.setDialogAnamnese(true);
                    this.setDialogAnamneseListItem(true);
                    break;
                }
                case Satzart.Brief: {
                    this.setSelectedBrief(eintrag);
                    this.setDialogBrief(true);
                    break;
                }
                case Satzart.Rezept: {
                    this.setSelectedRezepteintrag(eintrag);
                    this.setDialogRezept(true);
                    break;
                }
            }
        }

        public onClickExpand(eintrag: Karteieintrag): void {
            if (this.dtExpand.length === 0) {
                this.dtExpand = [eintrag];
            } else {
                if (this.dtExpand.indexOf(eintrag) !== -1) {
                    this.dtExpand = [];
                } else {
                    this.dtExpand = [eintrag];
                }
            }
        }

        public getTextDisplayClasses(eintrag: Karteieintrag): string {
            switch (eintrag.satzart) {
                case Satzart.Plan:
                    return 'text-plan';
                case Satzart.Impfung:
                    return '';
                case Satzart.Leistung:
                    return 'text-leistung';
                case Satzart.Text:
                    return 'text-text text-truncate dots';
                case Satzart.Untersuchung:
                    return 'text-untersuchung';
                case Satzart.Diagnose:
                    return 'text-diagnose';
                case Satzart.Anamnese:
                    return 'text-anamnese';
                case Satzart.Brief:
                    return 'text-brief';
                case Satzart.Rezept:
                    return 'text-rezept';
                default:
                    return '';
            }
        }

        public getElgaStatusColor(eintrag: Karteieintrag): string | undefined {
            enum StatusColor {
                NichtUebertragen = '#d32f2f',
                WirdUebertragenOderStorniert = '#e2b24b',
                Uebertragen = '#4caf50',
            }

            const zuUebertragendeImpfung = eintrag.isImpfung &&
                !eintrag.isElgaImpfung &&
                !eintrag.isNachtrag &&
                eintrag.elga_impfstoff_code &&
                eintrag.person_id_durchf &&
                (eintrag.impfdatum ?? '').slice(0, 10) >= (this.user?.release_date ?? '');

            if (!zuUebertragendeImpfung) {
                return undefined;
            }

            if (eintrag.isInTransfer || eintrag.isInStornierung) {
                return StatusColor.WirdUebertragenOderStorniert;
            }

            return eintrag.eimpf_id !== null
                ? StatusColor.Uebertragen
                : StatusColor.NichtUebertragen;
        }

        public getTextDownloadStatus(eintrag: Karteieintrag): boolean {
            return this.loaders.text.downloads.indexOf(eintrag.patimpfung_id as number) !== -1;
        }

        public clickOnAnamnese(): void {
            this.setDialogAnamnese(true);
            this.setDialogAnamneseListItem(false);
        }
    }
